import { Search, SearchIcon } from "lucide-react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Card } from "./ui/card";
import { WooCommerce } from "../lib/api";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "./ui/input";
import MyLoader from "./MyLoader";
import { Skeleton } from "./ui/skeleton";
import { wooProduct } from "../pages/products/productInterface";
import { Button } from "./ui/button";

export default function SearchSheet({
  scrolled,
}: {
  scrolled?: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showInput, setShowInput] = useState(true);
  const [loader, setLoader] = useState(false);
  const [foundProducts, setFoundProducts] =
    useState<any[]>();
  const nav = useNavigate();
  const [debouncedQuery, setDebouncedQuery] =
    useState(searchQuery);

  // Debounce effect
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500); // 500ms delay after user stops typing

    return () => clearTimeout(timer); // Clear previous timer on change
  }, [searchQuery]);

  // This will be called once user stops typing for 500ms
  React.useEffect(() => {
    if (debouncedQuery) {
      searchWooProducts();
    }
  }, [debouncedQuery]);
  const searchWooProducts = async () => {
    setLoader(true);
    setFoundProducts([]);

    let currentPage = 1;
    let hasMoreProducts = true;
    let allProducts: wooProduct[] = [];

    try {
      while (hasMoreProducts) {
        const response = await WooCommerce.get(
          `products?search=${searchQuery}&per_page=100&page=${currentPage}`
        );

        const products = response.data;

        if (products && products.length > 0) {
          // Add products to our collection
          allProducts = [...allProducts, ...products];
          currentPage++;
        } else {
          // No more products, exit loop
          hasMoreProducts = false;
        }
      }

      setFoundProducts(allProducts);
      setShowInput(false);
      return allProducts;
    } catch (error) {
      console.error("Could not search products:", error);
      return [];
    } finally {
      setLoader(false);
    }
  };
  React.useEffect(() => {
    const listener = (event: any) => {
      if (
        event.code === "Enter" ||
        event.code === "NumpadEnter"
      ) {
        nav(`/shop/${searchQuery}`);
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [searchQuery]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <SearchIcon
          className={
            scrolled
              ? "stroke-1 sm:h-5 sm:w-5 w-6 h-6  text-neutral-900  hover:fill-neutral-900 dark:hover:fill-neutral-200 dark:text-neutral-200"
              : "stroke-1 sm:h-5 sm:w-5 w-6 h-6 text-neutral-900 hover:fill-neutral-900 "
          }
        />
      </SheetTrigger>
      <SheetContent
        side={"top"}
        className="font-montserrat"
      >
        <SheetHeader>
          <SheetTitle>SEARCH PRODUCTS</SheetTitle>
        </SheetHeader>
        <SheetDescription></SheetDescription>
        <Input
          className="focus:outline-none bg-transparent text-neutral-900 placeholder:text-neutral-600 dark:text-neutral-200 border-0 border-b dark:border-neutral-800 border-neutral-900 dark:placeholder:text-neutral-200 rounded-none"
          id="desktop-search"
          placeholder="Search..."
          type="search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />

        {searchQuery !== "" && (
          <div className="bg-white w-full mt-1 rounded-md dark:bg-neutral-900 ">
            <div className="flex justify-between w-full items-centerm mt-5 mb-5">
              {" "}
              <p className="text-sm font-medium">
                {`SEARCH RESULTS FOR ${searchQuery}`}
              </p>
              <SheetClose asChild>
                <Button
                  className="rounded-none float-right flex justify-center items-center gap-2"
                  onClick={() =>
                    nav(`/shop/${searchQuery}`)
                  }
                >
                  <Search className="size-5 dark:text-neutral-900 text-neutral-100 hover:fill-neutral-900 stroke-1" />
                  <span className="font-light">SEARCH</span>
                </Button>
              </SheetClose>
            </div>
            <div
              className={`flex flex-wrap gap-5  overflow-scroll no-scrollbar ${
                foundProducts &&
                foundProducts?.length > 0 &&
                "h-[400px]"
              }`}
            >
              {loader ? (
                <div className="flex justify-center items-center w-full h-24">
                  <MyLoader />
                </div>
              ) : (
                foundProducts?.map((product, index) => (
                  <SheetClose asChild key={index}>
                    {!showInput ? (
                      <Card
                        className="h-fit flex p-0 items-center hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer  w-96 rounded-none"
                        onClick={() => {
                          nav(
                            `/product/${product.categories[0]?.id}/${product.categories[0]?.name}/${product.id}/${product.slug}`
                          );
                          setShowInput(false);
                          setSearchQuery("");
                        }}
                      >
                        <div className="flex justify-center items-center gap-2 w-full h-full">
                          <div className="size-20 flex justify-center items-center dark:bg-white">
                            {product && product.images && (
                              <img
                                alt="search-products-img"
                                src={
                                  product?.images[0]?.src ||
                                  "/logo.png"
                                }
                                className="w-full h-full"
                              />
                            )}
                          </div>
                          <div className="flex flex-col justify-between h-full gap-1 sm:text-md text-sm w-full font-medium">
                            <h2 className=" w-full">
                              {product.name.toLocaleUpperCase()}
                            </h2>

                            <p className="text-sm font-montserrat font-bold">
                              {product.price}.00$
                            </p>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <div className="flex gap-5 justify-center items-center flex-wrap">
                        <div className="flex gap-2 w-full items-center">
                          <Skeleton className="w-16 h-16 rounded-full" />
                          <div className="flex flex-col gap-2">
                            <Skeleton className="w-24 h-4" />
                            <Skeleton className="w-12 h-2" />
                          </div>
                        </div>
                      </div>
                    )}
                  </SheetClose>
                ))
              )}

              {!loader &&
              foundProducts &&
              foundProducts?.length < 1 ? (
                <p>No products found!</p>
              ) : (
                <p></p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
